import React, { useState, useCallback, useEffect } from 'react';
import { Title, useGetOne, useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Backdrop, Box, CircularProgress, Typography } from '@mui/material';

import { getCurrentPartner } from '@src/providers/dataProvider/shared/partner';
import type { PartnerSettings } from '@src/settings';

import {
  cancelCatalogUpload,
  scheduleCatalogUpload,
  getCatalogUploads,
  getCatalogUploadDownloadUrl,
  validateCatalogUpload,
} from '@src/apis/reware/catalog';
import type { CatalogUploadListItem } from '@src/apis/reware/catalog';
import type { CatalogUploadValidation } from '@src/apis/reware/catalog/validateCatalogUpload';

import CsvDropZone from './CsvDropZone';
import ConfirmUploadDialog from './ConfirmUploadDialog';
import UploadHistoryTable from './UploadHistoryTable';
import ValidationResults from './ValidationResults';
import type { UploaderPageState, UploadOptions } from './types';

const CatalogUploaderPage: React.FC = () => {
  const notify = useNotify();
  const navigate = useNavigate();
  const partner = getCurrentPartner();
  const { data: settings } = useGetOne<PartnerSettings & { id: number }>('settings', { id: 0 });

  // Page state machine
  const [pageState, setPageState] = useState<UploaderPageState>('idle');

  // File selection state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(false);

  // Validation state
  const [validation, setValidation] = useState<CatalogUploadValidation | null>(null);
  const [uploadOptions, setUploadOptions] = useState<UploadOptions | null>(null);
  const [validationId, setValidationId] = useState<string | null>(null);

  // Preview pagination state
  const [previewPageSize, setPreviewPageSize] = useState(50);
  const [cursorHistory, setCursorHistory] = useState<(string | null)[]>([null]);

  // Upload history state
  const [uploads, setUploads] = useState<CatalogUploadListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);

  const loadUploads = useCallback(async () => {
    if (!partner?.id) return;
    setLoading(true);
    try {
      const result = await getCatalogUploads(partner.id, page, pageSize);
      setUploads(result.catalogUploads.rows ?? []);
      setTotalCount(result.catalogUploads.count ?? 0);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Failed to load upload history', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [partner?.id, page, pageSize, notify]);

  useEffect(() => {
    void loadUploads();
  }, [loadUploads]);

  const handleReset = () => {
    setPageState('idle');
    setSelectedFile(null);
    setValidation(null);
    setUploadOptions(null);
    setValidationId(null);
    setDialogOpen(false);
    setPreviewPageSize(50);
    setCursorHistory([null]);
  };

  const handleFileSelected = (file: File) => {
    setSelectedFile(file);
    setDialogOpen(true);
  };

  const handleDialogCancel = () => {
    setDialogOpen(false);
    if (pageState === 'idle') {
      setSelectedFile(null);
      setValidation(null);
    }
  };

  const handleOptionsConfirm = async (options: UploadOptions) => {
    if (!selectedFile || !partner?.code) return;

    setUploadOptions(options);
    setDialogOpen(false);
    setIsValidating(true);

    try {
      const result = await validateCatalogUpload(
        {
          partnerCode: partner.code,
          source: options.uploadType,
          first: previewPageSize,
          after: null,
        },
        selectedFile,
      );
      setValidation(result);
      setValidationId(result.validationId);
      setPageState('validating');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Validation failed', { type: 'error' });
    } finally {
      setIsValidating(false);
    }
  };

  const handleBeginUpload = async () => {
    if (!selectedFile || !partner?.code || !uploadOptions) return;

    setIsUploading(true);
    try {
      const ext = selectedFile.name.match(/\.(csv|tsv)$/i)?.[0] ?? '.csv';
      const uploadName = uploadOptions.uploadName.endsWith(ext) ?
        uploadOptions.uploadName :
        `${uploadOptions.uploadName}${ext}`;
      const renamedFile = new File([selectedFile], uploadName, { type: selectedFile.type });

      const result = await scheduleCatalogUpload(
        {
          partnerCode: partner.code,
          source: uploadOptions.uploadType,
          scheduleAt: uploadOptions.scheduleAt,
          timezone: uploadOptions.timezone,
          totalRowCount: validation?.rowCount,
        },
        renamedFile,
      );

      if (result.scheduleCatalogUpload.result) {
        notify('Upload submitted successfully', { type: 'success' });

        // TODO: Backend should return the upload ID directly from scheduleCatalogUpload.
        // Workaround: fetch the most recent upload to get its ID for navigation.
        if (partner.id) {
          const uploadsResult = await getCatalogUploads(partner.id, 1, 1);
          const newUpload = uploadsResult.catalogUploads.rows[0];
          if (newUpload) {
            handleReset();
            void navigate(`/catalog-uploader/${newUpload.id}`);
            return;
          }
        }

        handleReset();
        void loadUploads();
      } else {
        notify('Upload failed', { type: 'error' });
      }
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Upload failed', { type: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelUpload = async (uploadId: number) => {
    try {
      const success = await cancelCatalogUpload(uploadId);
      if (success) {
        notify('Upload cancelled', { type: 'success' });
        void loadUploads();
      } else {
        notify('Failed to cancel upload', { type: 'error' });
      }
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Failed to cancel upload', { type: 'error' });
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleDownloadOriginal = async (uploadId: number) => {
    try {
      const url = await getCatalogUploadDownloadUrl(uploadId, 'location');
      if (url) {
        window.open(url, '_blank');
      } else {
        notify('Download URL not available', { type: 'warning' });
      }
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Failed to get download URL', { type: 'error' });
    }
  };

  const handleDownloadErrorReport = async (uploadId: number) => {
    try {
      const url = await getCatalogUploadDownloadUrl(uploadId, 'errorContents');
      if (url) {
        window.open(url, '_blank');
      } else {
        notify('Error report not available', { type: 'warning' });
      }
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Failed to get download URL', { type: 'error' });
    }
  };

  const handleRowClick = (uploadId: number) => {
    void navigate(`/catalog-uploader/${uploadId}`);
  };

  const handlePreviewNextPage = async () => {
    if (!validationId || !validation?.previewRecords || !partner?.code) return;

    const endCursor = validation.previewRecords.pageInfo.endCursor;
    if (!endCursor) return;

    setIsLoadingPage(true);

    try {
      const result = await validateCatalogUpload({
        partnerCode: partner.code,
        validationId,
        first: previewPageSize,
        after: endCursor,
      });
      setValidation(result);
      // Add the current end cursor to history for backward navigation
      setCursorHistory(prev => [...prev, endCursor]);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Failed to load preview records', { type: 'error' });
    } finally {
      setIsLoadingPage(false);
    }
  };

  const handlePreviewPreviousPage = async () => {
    if (!validationId || cursorHistory.length <= 1 || !partner?.code) return;

    setIsLoadingPage(true);

    try {
      // Remove the current page's cursor from history
      const newHistory = [...cursorHistory];
      newHistory.pop();
      setCursorHistory(newHistory);

      // Use the previous cursor (which is now the last one in the history)
      const previousCursor = newHistory[newHistory.length - 1];

      const result = await validateCatalogUpload({
        partnerCode: partner.code,
        validationId,
        first: previewPageSize,
        after: previousCursor,
      });
      setValidation(result);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Failed to load preview records', { type: 'error' });
    } finally {
      setIsLoadingPage(false);
    }
  };

  const handlePreviewPageSizeChange = async (newPageSize: number) => {
    if (!validationId || !partner?.code) return;

    setPreviewPageSize(newPageSize);
    setCursorHistory([null]); // Reset cursor history when changing page size
    setIsLoadingPage(true);

    try {
      const result = await validateCatalogUpload({
        partnerCode: partner.code,
        validationId,
        first: newPageSize,
        after: null,
      });
      setValidation(result);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Failed to load preview records', { type: 'error' });
    } finally {
      setIsLoadingPage(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Catalog Uploader" />

      {pageState === 'idle' && (
        <CsvDropZone
          onFileSelected={handleFileSelected}
          disabled={isUploading || isValidating}
          showDownloadTemplate={!!settings?.deployCatalogTemplateDefinitions}
          partnerCode={partner?.code}
          onError={message => notify(message, { type: 'error' })}
        />
      )}

      {pageState === 'validating' && validation && uploadOptions && (
        <ValidationResults
          options={uploadOptions}
          validation={validation}
          onEditOptions={() => setDialogOpen(true)}
          onCancel={handleReset}
          onBeginUpload={handleBeginUpload}
          isUploading={isUploading}
          previewPageSize={previewPageSize}
          onPreviewNextPage={handlePreviewNextPage}
          onPreviewPreviousPage={handlePreviewPreviousPage}
          onPreviewPageSizeChange={handlePreviewPageSizeChange}
        />
      )}

      <Box sx={{ mt: 4 }}>
        <UploadHistoryTable
          uploads={uploads}
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          loading={loading}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onCancelUpload={handleCancelUpload}
          onDownloadOriginal={handleDownloadOriginal}
          onDownloadErrorReport={handleDownloadErrorReport}
          onRowClick={handleRowClick}
        />
      </Box>

      {selectedFile && (
        <ConfirmUploadDialog
          open={dialogOpen}
          fileName={selectedFile.name}
          onConfirm={handleOptionsConfirm}
          onCancel={handleDialogCancel}
        />
      )}

      <Backdrop
        open={isValidating}
        sx={{
          color: '#fff',
          zIndex: (theme): number => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6">Validating file...</Typography>
        <Typography variant="body2">This may take a moment for large files</Typography>
      </Backdrop>

      <Backdrop
        open={isLoadingPage}
        sx={{
          color: '#fff',
          zIndex: (theme): number => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6">Loading preview...</Typography>
      </Backdrop>
    </Box>
  );
};

export default CatalogUploaderPage;
